// src/services/logistics.service.ts
import { db, storage } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  ProjectLogistics,
  LogisticsType,
  LogisticsStatus,
  LogisticsFilter,
  LogisticsSummary,
  HotelInfo,
  FlightInfo,
  RentalCarInfo,
  GroundTransportInfo
} from '../types/logistics.types';
import { createExpenseFromLogistics, deleteExpensesBySource, updateExpenseBySource } from './expense.service';

export const logisticsService = {
  async createLogistics(
    data: Omit<ProjectLogistics, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const logisticsRef = doc(collection(db, 'projectLogistics'));
    const logistics: ProjectLogistics = {
      ...data,
      id: logisticsRef.id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    await setDoc(logisticsRef, logistics);

    // Auto-create expense if there's a cost and a project is assigned
    if (data.totalCost && data.totalCost > 0 && data.projectId) {
      try {
        // Determine date based on logistics type
        let expenseDate = new Date();
        if (data.type === 'hotel' && data.hotel?.checkInDate) {
          expenseDate = new Date(data.hotel.checkInDate + 'T12:00:00');
        } else if (data.type === 'flight' && data.flight?.departureDate) {
          expenseDate = new Date(data.flight.departureDate + 'T12:00:00');
        } else if (data.type === 'rental_car' && data.rentalCar?.pickupDate) {
          expenseDate = new Date(data.rentalCar.pickupDate + 'T12:00:00');
        } else if (data.type === 'ground_transport' && data.groundTransport?.pickupDate) {
          expenseDate = new Date(data.groundTransport.pickupDate + 'T12:00:00');
        }

        await createExpenseFromLogistics(
          {
            description: `${data.type.charAt(0).toUpperCase() + data.type.slice(1).replace('_', ' ')}: ${data.title || 'Logistics'}`,
            amount: data.totalCost,
            projectId: data.projectId,
            projectName: data.projectName || '',
            date: expenseDate,
            sourceType: data.type as 'hotel' | 'flight' | 'rental_car' | 'ground_transport',
            sourceId: logisticsRef.id
          },
          data.createdBy || '',
          data.createdByName || ''
        );
      } catch (error) {
        console.error('Error creating expense from logistics:', error);
        // Don't fail the logistics creation if expense creation fails
      }
    }

    return logisticsRef.id;
  },

  async getLogistics(logisticsId: string): Promise<ProjectLogistics | null> {
    const docRef = doc(db, 'projectLogistics', logisticsId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as ProjectLogistics;
    }
    return null;
  },

  async getLogisticsList(filters?: LogisticsFilter): Promise<ProjectLogistics[]> {
    let q = query(collection(db, 'projectLogistics'), orderBy('createdAt', 'desc'));

    if (filters?.projectId) {
      q = query(q, where('projectId', '==', filters.projectId));
    }

    if (filters?.assignedTo) {
      q = query(q, where('assignedTo', '==', filters.assignedTo));
    }

    const snapshot = await getDocs(q);
    let logistics = snapshot.docs.map(doc => doc.data() as ProjectLogistics);

    // Apply additional filters
    if (filters?.type && filters.type.length > 0) {
      logistics = logistics.filter(l => filters.type!.includes(l.type));
    }

    if (filters?.status && filters.status.length > 0) {
      logistics = logistics.filter(l => filters.status!.includes(l.status));
    }

    if (filters?.dateFrom) {
      logistics = logistics.filter(l => {
        const startDate = l.hotel?.checkInDate || l.flight?.departureDate || l.rentalCar?.pickupDate;
        return startDate && startDate >= filters.dateFrom!;
      });
    }

    if (filters?.dateTo) {
      logistics = logistics.filter(l => {
        const startDate = l.hotel?.checkInDate || l.flight?.departureDate || l.rentalCar?.pickupDate;
        return startDate && startDate <= filters.dateTo!;
      });
    }

    return logistics;
  },

  async getUserLogistics(userId: string): Promise<ProjectLogistics[]> {
    const q = query(
      collection(db, 'projectLogistics'),
      where('assignedTo', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as ProjectLogistics);
  },

  async getProjectLogistics(projectId: string): Promise<ProjectLogistics[]> {
    const q = query(
      collection(db, 'projectLogistics'),
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as ProjectLogistics);
  },

  async updateLogistics(logisticsId: string, updates: Partial<ProjectLogistics>): Promise<void> {
    // Get current logistics to check for changes
    const currentLogistics = await this.getLogistics(logisticsId);

    await updateDoc(doc(db, 'projectLogistics', logisticsId), {
      ...updates,
      updatedAt: Timestamp.now()
    });

    // Sync expense if cost changed
    if (currentLogistics && updates.totalCost !== undefined) {
      try {
        const mergedData = { ...currentLogistics, ...updates };

        if (updates.totalCost > 0 && mergedData.projectId) {
          // Determine date based on logistics type
          let expenseDate = new Date();
          if (mergedData.type === 'hotel' && mergedData.hotel?.checkInDate) {
            expenseDate = new Date(mergedData.hotel.checkInDate + 'T12:00:00');
          } else if (mergedData.type === 'flight' && mergedData.flight?.departureDate) {
            expenseDate = new Date(mergedData.flight.departureDate + 'T12:00:00');
          } else if (mergedData.type === 'rental_car' && mergedData.rentalCar?.pickupDate) {
            expenseDate = new Date(mergedData.rentalCar.pickupDate + 'T12:00:00');
          } else if (mergedData.type === 'ground_transport' && mergedData.groundTransport?.pickupDate) {
            expenseDate = new Date(mergedData.groundTransport.pickupDate + 'T12:00:00');
          }

          const categoryMap: Record<string, { id: string; name: string }> = {
            flight: { id: 'travel_flight', name: 'Flights' },
            hotel: { id: 'travel_hotel', name: 'Hotels' },
            rental_car: { id: 'travel_rental', name: 'Car Rentals' },
            ground_transport: { id: 'other', name: 'Ground Transport' },
          };
          const category = categoryMap[mergedData.type] || { id: 'other', name: 'Other' };

          // Update existing expense or create new one if none exists
          await updateExpenseBySource('logistics', logisticsId, {
            description: `${mergedData.type.charAt(0).toUpperCase() + mergedData.type.slice(1).replace('_', ' ')}: ${mergedData.title || 'Logistics'}`,
            amount: updates.totalCost,
            date: expenseDate,
            categoryId: category.id,
            categoryName: category.name,
          });
        } else if (updates.totalCost === 0) {
          // If cost is set to 0, delete the expense
          await deleteExpensesBySource('logistics', logisticsId);
        }
      } catch (error) {
        console.error('Error syncing expense with logistics update:', error);
        // Don't fail the logistics update if expense sync fails
      }
    }
  },

  async updateLogisticsStatus(logisticsId: string, status: LogisticsStatus): Promise<void> {
    await updateDoc(doc(db, 'projectLogistics', logisticsId), {
      status,
      updatedAt: Timestamp.now()
    });
  },

  async deleteLogistics(logisticsId: string): Promise<void> {
    const logistics = await this.getLogistics(logisticsId);
    if (logistics?.attachments) {
      // Delete attachments from storage
      for (const attachment of logistics.attachments) {
        try {
          const storageRef = ref(storage, attachment.url);
          await deleteObject(storageRef);
        } catch (e) {
          // File may not exist
        }
      }
    }

    // Delete associated expense if any
    try {
      await deleteExpensesBySource('logistics', logisticsId);
    } catch (error) {
      console.error('Error deleting associated expense:', error);
    }

    await deleteDoc(doc(db, 'projectLogistics', logisticsId));
  },

  async uploadAttachment(
    logisticsId: string,
    file: File
  ): Promise<{ name: string; url: string; type: string }> {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const storageRef = ref(storage, `logistics/${logisticsId}/${fileName}`);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    const attachment = {
      name: file.name,
      url,
      type: file.type
    };

    // Add to logistics attachments array
    const logistics = await this.getLogistics(logisticsId);
    if (logistics) {
      const attachments = logistics.attachments || [];
      attachments.push(attachment);
      await updateDoc(doc(db, 'projectLogistics', logisticsId), {
        attachments,
        updatedAt: Timestamp.now()
      });
    }

    return attachment;
  },

  async getProjectLogisticsSummary(projectId: string, projectName: string): Promise<LogisticsSummary> {
    const logistics = await this.getProjectLogistics(projectId);

    const now = new Date();

    const summary: LogisticsSummary = {
      projectId,
      projectName,
      totalLogistics: logistics.length,
      pendingCount: logistics.filter(l => l.status === 'pending').length,
      confirmedCount: logistics.filter(l => l.status === 'confirmed').length,
      totalCost: logistics.reduce((sum, l) => sum + (l.totalCost || 0), 0),
      upcomingFlights: logistics.filter(l =>
        l.type === 'flight' &&
        l.flight?.departureDate &&
        new Date(l.flight.departureDate + 'T12:00:00') >= now
      ).length,
      activeHotels: logistics.filter(l =>
        l.type === 'hotel' &&
        l.hotel?.checkInDate &&
        l.hotel?.checkOutDate &&
        new Date(l.hotel.checkInDate + 'T12:00:00') <= now &&
        new Date(l.hotel.checkOutDate + 'T12:00:00') >= now
      ).length,
      activeRentals: logistics.filter(l =>
        l.type === 'rental_car' &&
        l.rentalCar?.pickupDate &&
        l.rentalCar?.dropoffDate &&
        new Date(l.rentalCar.pickupDate + 'T12:00:00') <= now &&
        new Date(l.rentalCar.dropoffDate + 'T12:00:00') >= now
      ).length
    };

    return summary;
  },

  async getUpcomingLogistics(userId: string, days = 30): Promise<ProjectLogistics[]> {
    const logistics = await this.getUserLogistics(userId);
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    return logistics.filter(l => {
      let startDate: string | undefined;
      switch (l.type) {
        case 'hotel':
          startDate = l.hotel?.checkInDate;
          break;
        case 'flight':
          startDate = l.flight?.departureDate;
          break;
        case 'rental_car':
          startDate = l.rentalCar?.pickupDate;
          break;
        case 'ground_transport':
          startDate = l.groundTransport?.pickupDate;
          break;
        default:
          startDate = l.otherDetails?.startDate;
      }

      if (!startDate) return false;
      const date = new Date(startDate + 'T12:00:00');
      return date >= now && date <= futureDate;
    }).sort((a, b) => {
      const getDate = (l: ProjectLogistics) => {
        switch (l.type) {
          case 'hotel': return l.hotel?.checkInDate;
          case 'flight': return l.flight?.departureDate;
          case 'rental_car': return l.rentalCar?.pickupDate;
          case 'ground_transport': return l.groundTransport?.pickupDate;
          default: return l.otherDetails?.startDate;
        }
      };
      return (getDate(a) || '').localeCompare(getDate(b) || '');
    });
  }
};
